import { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { API, useAuth } from '../App';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';
import {
  ChevronDown, ChevronRight, AlertTriangle, Download,
  ClipboardList, Loader2, MessageSquare, Clock, Calculator, Search
} from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from '@/components/ui/command';
import PackingTab from './PackingTab';
import WeeklySummaryTab from './WeeklySummaryTab';
import ClientPackingTab from './ClientPackingTab';
import { StickerLine } from '../components/Stickers';
import { normalizeCategory } from '../lib/categories';

const PRODUCTION_DAYS = ['FRI', 'SAT', 'SUN', 'MON', 'TUES', 'WED', 'THURS'];
const ALL_TABS = ['SUMMARY', 'ALL', ...PRODUCTION_DAYS, 'INGREDIENTS', 'CALCULATE', 'PACKING', 'CLIENT_PACKING'];

const formatWeight = (g, weightType) => {
  if (weightType === 'pieces' || weightType === 'count') {
    const val = g % 1 === 0 ? g : g.toFixed(1);
    return `${val} pcs`;
  }
  if (g >= 1000) return `${(g / 1000).toFixed(2)}kg`;
  return `${Math.round(g)}g`;
};

const ProductionPage = () => {
  const { isAdmin, user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [locations, setLocations] = useState([]);
  const [activeLocationId, setActiveLocationId] = useState('');
  const [selectedOrderId, setSelectedOrderId] = useState('');
  const [activeTab, setActiveTab] = useState('SUMMARY');
  const [sheetData, setSheetData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expandedRecipes, setExpandedRecipes] = useState(new Set());
  const [expandedSubRecipes, setExpandedSubRecipes] = useState(new Set());
  const [lineStatuses, setLineStatuses] = useState({});
  const [subRecipeStatuses, setSubRecipeStatuses] = useState({});
  const [selectedSubRecipes, setSelectedSubRecipes] = useState(new Set());
  const [exportingSR, setExportingSR] = useState(false);
  const [issueText, setIssueText] = useState('');
  const [issues, setIssues] = useState([]);
  
  // Calculator state
  const [allSubRecipes, setAllSubRecipes] = useState([]);
  const [allRecipes, setAllRecipes] = useState([]);
  const [calcSubRecipeId, setCalcSubRecipeId] = useState('');
  const [calcWeight, setCalcWeight] = useState('');
  const [calcResult, setCalcResult] = useState(null);
  const [calcSearchOpen, setCalcSearchOpen] = useState(false);
  // Final recipe calculator
  const [calcRecipeId, setCalcRecipeId] = useState('');
  const [calcRecipeQty, setCalcRecipeQty] = useState('');
  const [calcRecipeSize, setCalcRecipeSize] = useState('');
  const [calcRecipeResult, setCalcRecipeResult] = useState(null);
  const [calcRecipeSearchOpen, setCalcRecipeSearchOpen] = useState(false);
  const [expandedCalcComponents, setExpandedCalcComponents] = useState(new Set());

  // Feature 2: Prep Planner state
  const [prepDays, setPrepDays] = useState(new Set());
  const [prepClients, setPrepClients] = useState(new Set());
  const [prepAvailableClients, setPrepAvailableClients] = useState([]);
  const [prepData, setPrepData] = useState(null);
  const [prepLoading, setPrepLoading] = useState(false);
  const [prepSearch, setPrepSearch] = useState('');
  const [prepExpanded, setPrepExpanded] = useState(true);
  const [expandedPrepSubs, setExpandedPrepSubs] = useState(new Set());

  // Feature 3: Client Breakdown state
  const [cbDays, setCbDays] = useState(new Set());
  const [cbClients, setCbClients] = useState(new Set());
  const [cbAvailableClients, setCbAvailableClients] = useState([]);
  const [cbData, setCbData] = useState(null);
  const [cbLoading, setCbLoading] = useState(false);
  const [expandedCbRecipes, setExpandedCbRecipes] = useState(new Set());
  const [expandedCbChildren, setExpandedCbChildren] = useState(new Set());

  // Feature 4: Multi-size calculator state
  const [calcMultiSizeQtys, setCalcMultiSizeQtys] = useState({});
  const [calcMultiResult, setCalcMultiResult] = useState(null);
  const [expandedMultiComponents, setExpandedMultiComponents] = useState(new Set());

  useEffect(() => {
    axios.get(`${API}/weekly-orders`).then(r => {
      setOrders(r.data);
    }).catch(() => {});
    axios.get(`${API}/locations`).then(r => setLocations(r.data)).catch(() => {});
    axios.get(`${API}/sub-recipes`).then(r => setAllSubRecipes(r.data)).catch(() => {});
    axios.get(`${API}/recipes`).then(r => setAllRecipes(r.data)).catch(() => {});
  }, []);

  // Set default active location once user + orders are known
  useEffect(() => {
    if (activeLocationId) return;
    if (orders.length === 0) return;
    const defaultLoc = (isAdmin && user?.location_id) ? user.location_id : (orders[0].location_id || 'main-kitchen');
    setActiveLocationId(defaultLoc);
  }, [orders, isAdmin, user, activeLocationId]);

  // Orders filtered to the current active location
  const ordersInLocation = orders.filter(o => (o.location_id || 'main-kitchen') === activeLocationId);

  // Auto-select the most recent order in the active location whenever it changes
  useEffect(() => {
    if (!activeLocationId) return;
    if (ordersInLocation.length === 0) {
      setSelectedOrderId('');
      return;
    }
    // If the currently selected order isn't in this location, jump to the newest
    const stillValid = ordersInLocation.some(o => o.id === selectedOrderId);
    if (!stillValid) {
      setSelectedOrderId(ordersInLocation[0].id);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeLocationId, orders]);

  const activeLocationName = locations.find(l => l.id === activeLocationId)?.name
    || (activeLocationId === 'main-kitchen' ? 'Main Kitchen' : activeLocationId);

  const fetchSheet = useCallback(async () => {
    if (!selectedOrderId) return;
    if (activeTab === 'CALCULATE') return;
    if (activeTab === 'PACKING') return;
    if (activeTab === 'CLIENT_PACKING') return;
    if (activeTab === 'SUMMARY') return;
    const fetchDay = activeTab === 'INGREDIENTS' ? 'ALL' : activeTab;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/production/${selectedOrderId}/${fetchDay}`);
      setSheetData(r.data);
      // Filter statuses to only current line IDs for accurate progress
      const currentLineIds = new Set(r.data.final_recipes?.map(fr => fr.line_id) || []);
      const filteredStatuses = {};
      Object.entries(r.data.status?.line_statuses || {}).forEach(([k, v]) => {
        if (currentLineIds.has(k)) filteredStatuses[k] = v;
      });
      setLineStatuses(filteredStatuses);
      setSubRecipeStatuses(r.data.status?.sub_recipe_statuses || {});
      setIssues(r.data.status?.issues || []);
    } catch (e) { toast.error('Failed to load production sheet'); }
    finally { setLoading(false); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrderId, activeTab]);

  useEffect(() => { fetchSheet(); }, [fetchSheet]);

  const toggleExpand = (id, set, setter) => {
    const next = new Set(set);
    if (next.has(id)) next.delete(id); else next.add(id);
    setter(next);
  };

  // Progress tracking
  const toggleLineStatus = async (lineId) => {
    const updated = { ...lineStatuses, [lineId]: !lineStatuses[lineId] };
    setLineStatuses(updated);
    const completedCount = Object.values(updated).filter(Boolean).length;
    const totalCount = sheetData?.final_recipes?.length || 0;
    const overall = completedCount === 0 ? 'not_started' : completedCount >= totalCount ? 'complete' : 'in_progress';
    await axios.put(`${API}/production/${selectedOrderId}/${activeTab}/status`, {
      line_statuses: updated, overall_status: overall
    }).catch(() => {});
  };

  const toggleSubRecipeStatus = async (srId) => {
    const updated = { ...subRecipeStatuses, [srId]: !subRecipeStatuses[srId] };
    setSubRecipeStatuses(updated);
    await axios.put(`${API}/production/${selectedOrderId}/${activeTab}/status`, {
      sub_recipe_statuses: updated
    }).catch(() => {});
  };

  const addIssue = async () => {
    if (!issueText.trim()) return;
    try {
      const r = await axios.post(`${API}/production/${selectedOrderId}/${activeTab}/issues`, { text: issueText });
      setIssues(prev => [...prev, r.data]);
      setIssueText('');
      toast.success('Issue logged');
    } catch (e) { toast.error('Failed to log issue'); }
  };

  // Calculator - Sub-Recipe
  const handleCalculate = async () => {
    if (!calcSubRecipeId || !calcWeight) return;
    try {
      const r = await axios.post(`${API}/production/calculate-sub-recipe`, {
        sub_recipe_id: calcSubRecipeId, desired_weight: parseFloat(calcWeight)
      });
      setCalcResult(r.data);
    } catch (e) { toast.error('Failed to calculate'); }
  };

  // Calculator - Final Recipe
  const handleCalculateRecipe = async () => {
    if (!calcRecipeId || !calcRecipeQty) return;
    try {
      const r = await axios.post(`${API}/production/calculate-final-recipe`, {
        recipe_id: calcRecipeId, quantity: parseInt(calcRecipeQty), meal_size: calcRecipeSize
      });
      setCalcRecipeResult(r.data);
    } catch (e) { toast.error('Failed to calculate'); }
  };

  // Feature 2: Prep Planner
  const togglePrepDay = (day) => {
    setPrepDays(prev => {
      const next = new Set(prev);
      if (next.has(day)) next.delete(day); else next.add(day);
      loadPrepClients(next);
      return next;
    });
  };

  const handlePrepGenerate = async () => {
    if (prepDays.size === 0 || !selectedOrderId) return;
    setPrepLoading(true);
    try {
      const r = await axios.post(`${API}/production/${selectedOrderId}/prep-totals`, {
        days: [...prepDays],
        client_names: [...prepClients]
      });
      setPrepData(r.data);
      setPrepAvailableClients(r.data.available_clients || []);
    } catch (e) { toast.error('Failed to generate prep totals'); }
    finally { setPrepLoading(false); }
  };

  const loadPrepClients = async (days) => {
    if (days.size === 0 || !selectedOrderId) return;
    try {
      const r = await axios.post(`${API}/production/${selectedOrderId}/prep-totals`, {
        days: [...days], client_names: []
      });
      setPrepAvailableClients(r.data.available_clients || []);
    } catch (e) { /* ignore */ }
  };

  const filteredPrepItems = useMemo(() => {
    if (!prepData) return { raw: [], sub: [] };
    const q = prepSearch.toLowerCase();
    return {
      raw: prepData.raw_ingredients.filter(i => !q || i.ingredient_name.toLowerCase().includes(q)),
      sub: prepData.sub_recipes.filter(s => !q || s.name.toLowerCase().includes(q)),
    };
  }, [prepData, prepSearch]);

  // Feature 3: Client Breakdown
  const toggleCbDay = (day) => {
    setCbDays(prev => {
      const next = new Set(prev);
      if (next.has(day)) next.delete(day); else next.add(day);
      return next;
    });
  };

  const loadCbClients = async (days) => {
    if (days.size === 0 || !selectedOrderId) return;
    try {
      const r = await axios.post(`${API}/production/${selectedOrderId}/client-breakdown`, {
        days: [...days], client_names: []
      });
      setCbAvailableClients(r.data.available_clients || []);
    } catch (e) { /* ignore */ }
  };

  const handleCbGenerate = async () => {
    if (cbDays.size === 0 || !selectedOrderId) return;
    setCbLoading(true);
    try {
      const r = await axios.post(`${API}/production/${selectedOrderId}/client-breakdown`, {
        days: [...cbDays], client_names: [...cbClients]
      });
      setCbData(r.data);
      setCbAvailableClients(r.data.available_clients || []);
    } catch (e) { toast.error('Failed to generate breakdown'); }
    finally { setCbLoading(false); }
  };

  // Feature 4: Multi-Size Calculator
  const handleCalculateMulti = async () => {
    if (!calcRecipeId) return;
    const sizeQuantities = Object.entries(calcMultiSizeQtys)
      .filter(([, qty]) => parseInt(qty) > 0)
      .map(([size, qty]) => ({ meal_size: size, quantity: parseInt(qty) }));
    if (sizeQuantities.length === 0) { toast.error('Enter at least one quantity'); return; }
    try {
      const r = await axios.post(`${API}/production/calculate-final-recipe-multi`, {
        recipe_id: calcRecipeId, size_quantities: sizeQuantities
      });
      setCalcMultiResult(r.data);
    } catch (e) { toast.error('Failed to calculate'); }
  };

  // Export calculator results as Excel
  const exportCalcResult = (type) => {
    if (type === 'sub' && !calcResult) return;
    if (type === 'recipe' && !calcRecipeResult) return;
    
    // Build CSV and trigger download
    let csvContent = '';
    const now = new Date().toISOString().split('T')[0];
    let filename = '';
    
    if (type === 'sub') {
      filename = `SubRecipe_${calcResult.sub_recipe_name.replace(/[^a-zA-Z0-9]/g, '_')}_${now}.csv`;
      csvContent = `Sub-Recipe Calculator: ${calcResult.sub_recipe_name}\nBatch Yield: ${calcResult.yield_amount}${calcResult.yield_type}\nQuantity Needed: ${formatWeight(calcResult.desired_weight)}\nScale Factor: ${calcResult.scale_factor}x\n\nIngredient,Total Needed\n`;
      const addChildren = (items, indent) => {
        items.forEach(ing => {
          csvContent += `${indent}${ing.ingredient_name || ing.component_name},${formatWeight(ing.calculated_weight || ing.scaled_weight)}\n`;
          if (ing.children?.length > 0) addChildren(ing.children, indent + '  ');
        });
      };
      addChildren(calcResult.ingredients, '');
    } else {
      const cr = calcRecipeResult;
      filename = `FinalRecipe_${cr.recipe_name.replace(/[^a-zA-Z0-9]/g, '_')}_${cr.meal_size}_x${cr.quantity}_${now}.csv`;
      csvContent = `Final Recipe Calculator: ${cr.recipe_name}\nSize: ${cr.meal_size}\nQuantity: ${cr.quantity} portions\n\nComponent,Type,Total Weight\n`;
      cr.components.forEach(c => {
        csvContent += `${c.component_name},${c.component_type},${formatWeight(c.total_weight)}\n`;
        const addNested = (children, indent) => {
          children.forEach(ch => {
            csvContent += `${indent}${ch.ingredient_name},${ch.component_type},${formatWeight(ch.scaled_weight)}\n`;
            if (ch.children?.length > 0) addNested(ch.children, indent + '  ');
          });
        };
        if (c.children?.length > 0) addNested(c.children, '  ');
      });
    }
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  };

  const exportIngredients = () => {
    if (!selectedOrderId) return;
    window.open(`${API}/production/${selectedOrderId}/export/ingredients`, '_blank');
  };

  const exportDailySheet = (day) => {
    if (!selectedOrderId) return;
    window.open(`${API}/production/${selectedOrderId}/export/${day}`, '_blank');
  };

  const toggleSubRecipeSelection = (id) => {
    setSelectedSubRecipes(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const selectAllSubRecipes = () => {
    const ids = (sheetData?.sub_recipes || []).map(sr => sr.id);
    setSelectedSubRecipes(new Set(ids));
  };

  const deselectAllSubRecipes = () => setSelectedSubRecipes(new Set());

  const exportSelectedSubRecipes = async () => {
    if (selectedSubRecipes.size === 0 || !selectedOrderId) return;
    setExportingSR(true);
    try {
      const res = await axios.post(
        `${API}/production/${selectedOrderId}/export/sub-recipes`,
        { sub_recipe_ids: [...selectedSubRecipes] },
        { responseType: 'blob' }
      );
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Sub_Recipes_${sheetData?.week_label || 'Week'}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Sub-recipes exported');
    } catch (e) {
      toast.error('Failed to export sub-recipes');
    } finally {
      setExportingSR(false);
    }
  };

  // Progress stats — only count current line IDs
  const completedLines = Object.values(lineStatuses).filter(Boolean).length;
  const totalLines = sheetData?.final_recipes?.length || 0;
  const progressPct = totalLines > 0 ? Math.min(100, Math.round((completedLines / totalLines) * 100)) : 0;

  // Group final recipes by category
  const groupedRecipes = useMemo(() => {
    const grouped = {};
    (sheetData?.final_recipes || []).forEach(fr => {
      const cat = normalizeCategory(fr.category) || 'Other';
      if (!grouped[cat]) grouped[cat] = [];
      grouped[cat].push(fr);
    });
    return grouped;
  }, [sheetData?.final_recipes]);

  // Group raw ingredients by supplier for Ingredients tab
  const ingredientsBySupplier = useMemo(() => {
    const grouped = {};
    (sheetData?.raw_ingredients || []).forEach(ri => {
      const supplier = ri.supplier || '';
      if (!grouped[supplier]) grouped[supplier] = [];
      grouped[supplier].push(ri);
    });
    // Add sub-recipes as "Sub-Recipe" supplier group
    if (sheetData?.sub_recipes?.length > 0) {
      grouped['Sub-Recipe'] = sheetData.sub_recipes.map(sr => ({
        ingredient_name: sr.name,
        total_weight: sr.total_weight,
        supplier: 'Sub-Recipe',
        unit_type: sr.yield_type || 'g',
        used_in: sr.used_in?.map(u => u.dish) || [],
      }));
    }
    return grouped;
  }, [sheetData?.raw_ingredients, sheetData?.sub_recipes]);

  const isDayTab = PRODUCTION_DAYS.includes(activeTab);

  // Recursive tree renderer for calculator results
  const renderCalcTree = (items, depth) => {
    if (!items || items.length === 0) return null;
    return (
      <div className={depth > 0 ? 'border-l-2 border-gray-200 ml-2 pl-3' : ''}>
        {items.map((item, i) => (
          <div key={item.ingredient_name || item.component_name || i} className="py-1">
            <div className="flex items-center justify-between">
              <span className={`text-sm ${depth === 0 ? 'font-medium' : ''}`}>
                {item.ingredient_name || item.component_name}
                {item.component_type === 'sub_recipe' && <span className="ml-1 badge-pill badge-teal text-[9px]">Sub-Recipe</span>}
              </span>
              <span className="tabular-nums font-bold text-base ml-4 shrink-0">{formatWeight(item.calculated_weight || item.scaled_weight)}</span>
            </div>
            {item.children?.length > 0 && renderCalcTree(item.children, depth + 1)}
          </div>
        ))}
      </div>
    );
  };

  // Reusable SOP display block (shown above component lists)
  const renderSopBlock = (sopText, testId) => {
    if (!sopText || !sopText.trim()) return null;
    return (
      <div className="mb-3 p-3 border-l-4 border-amber-400 bg-amber-50 rounded" data-testid={testId || 'sop-block'}>
        <div className="text-[10px] uppercase tracking-wider font-bold text-amber-700 mb-1">SOP</div>
        <div className="text-sm whitespace-pre-wrap text-gray-800">{sopText}</div>
      </div>
    );
  };

  return (
    <div className="max-w-[1600px] mx-auto p-4 sm:p-6 lg:p-8" data-testid="production-page">
      {/* Header */}
      <div className="flex items-start justify-between mb-4 gap-4 flex-wrap">
        <div className="flex-1 min-w-[300px]">
          <h1 className="text-2xl sm:text-3xl tracking-tight font-bold text-gray-900" style={{ fontFamily: 'var(--font-heading)' }}>
            Production Sheets
          </h1>
          <p className="text-sm text-gray-500 mt-1">Daily production breakdown with sub-recipe totals and ingredient weights</p>
          {/* Persistent location + week context badge (visible to all users) */}
          {selectedOrderId && (
            <div className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs font-medium" data-testid="prod-location-badge">
              <span>📍 {activeLocationName}</span>
              <span className="text-emerald-400">·</span>
              <span>{orders.find(o => o.id === selectedOrderId)?.week_label || ''}</span>
            </div>
          )}
        </div>
        <div className="flex flex-col items-end gap-2">
          <Select value={selectedOrderId || ''} onValueChange={setSelectedOrderId}>
            <SelectTrigger className="w-[260px]" data-testid="order-selector">
              <SelectValue placeholder={ordersInLocation.length === 0 ? 'No orders in this location' : 'Select week...'} />
            </SelectTrigger>
            <SelectContent>
              {ordersInLocation.map(o => (
                <SelectItem key={o.id} value={o.id}>{o.week_label} ({o.line_count} dishes)</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Location pills — admin only. Instantly switches the week dropdown + selected order. */}
      {isAdmin && locations.length > 0 && (
        <div className="mb-6 flex items-center gap-2 flex-wrap" data-testid="prod-location-pills">
          <span className="text-xs uppercase tracking-wider font-semibold text-gray-500 mr-1">Location:</span>
          {locations.filter(l => l.active).map(l => {
            const count = orders.filter(o => (o.location_id || 'main-kitchen') === l.id).length;
            const isActive = l.id === activeLocationId;
            return (
              <button
                key={l.id}
                onClick={() => setActiveLocationId(l.id)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                  isActive
                    ? 'bg-emerald-600 text-white border-emerald-600'
                    : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
                }`}
                data-testid={`prod-loc-pill-${l.id}`}
              >
                📍 {l.name} <span className={`ml-1 ${isActive ? 'text-emerald-100' : 'text-gray-400'}`}>({count})</span>
              </button>
            );
          })}
        </div>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-4 flex-wrap" style={{ borderRadius: '8px' }}>
          {ALL_TABS.map(day => (
            <TabsTrigger key={day} value={day} style={{ borderRadius: '6px' }} className="text-xs"
              data-testid={`prod-tab-${day}`}>
              {day === 'ALL' ? 'ALL (Weekly)' : day === 'INGREDIENTS' ? 'Ingredients' : day === 'CALCULATE' ? 'Calculate' : day === 'PACKING' ? 'Packing' : day === 'CLIENT_PACKING' ? 'Client Packing' : day === 'SUMMARY' ? 'Weekly Summary' : day}
            </TabsTrigger>
          ))}
        </TabsList>

        {/* ============ WEEKLY SUMMARY TAB ============ */}
        <TabsContent value="SUMMARY">
          <WeeklySummaryTab selectedOrderId={selectedOrderId} />
        </TabsContent>

        {/* ============ CALCULATE TAB ============ */}
        <TabsContent value="CALCULATE" className="pb-16">
          {/* ===== FEATURE 2: PREP PLANNER ===== */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-3 cursor-pointer" onClick={() => setPrepExpanded(!prepExpanded)}>
              <div>
                <h2 className="text-lg font-semibold" style={{ fontFamily: 'var(--font-heading)' }}>Prep Planner</h2>
                <p className="text-xs text-gray-500 mt-0.5">Total ingredient and sub-recipe quantities by client and production day</p>
              </div>
              {prepExpanded ? <ChevronDown className="w-5 h-5 text-gray-400" /> : <ChevronRight className="w-5 h-5 text-gray-400" />}
            </div>
            {prepExpanded && (
              <div className="bg-white border border-gray-200 rounded-lg p-4" data-testid="prep-planner">
                <div className="flex flex-wrap items-center gap-2 mb-4">
                  <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider mr-1">Days:</span>
                  {PRODUCTION_DAYS.map(d => (
                    <button key={d} onClick={() => togglePrepDay(d)}
                      className={`px-3 py-1.5 text-xs font-semibold rounded-full border transition-colors ${prepDays.has(d) ? 'bg-[var(--color-primary)] text-white border-[var(--color-primary)]' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-400'}`}
                      data-testid={`prep-day-${d}`}>{d}</button>
                  ))}
                  <Button onClick={handlePrepGenerate} disabled={prepDays.size === 0 || prepLoading || !selectedOrderId} className="btn-primary text-xs h-8 ml-2" data-testid="prep-generate-btn">
                    {prepLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Calculator className="w-3.5 h-3.5 mr-1" />}
                    Generate
                  </Button>
                </div>
                {/* Client filter */}
                {prepAvailableClients.length > 0 && (
                  <div className="mb-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Filter by Client ({prepClients.size > 0 ? `${prepClients.size} selected` : 'All'})</span>
                      <div className="flex gap-2">
                        <button onClick={() => setPrepClients(new Set(prepAvailableClients))} className="text-xs text-[var(--color-primary)] hover:underline" data-testid="prep-select-all">Select All</button>
                        <button onClick={() => setPrepClients(new Set())} className="text-xs text-gray-400 hover:underline" data-testid="prep-deselect-all">Clear</button>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {prepAvailableClients.map(name => (
                        <button key={name} onClick={() => setPrepClients(prev => { const n = new Set(prev); if (n.has(name)) n.delete(name); else n.add(name); return n; })}
                          className={`px-2.5 py-1 text-xs rounded-full border transition-colors ${prepClients.has(name) ? 'bg-[var(--color-primary-50)] text-[var(--color-primary-dark)] border-[var(--color-primary)]' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-400'}`}
                          data-testid={`prep-client-${name}`}>{name}</button>
                      ))}
                    </div>
                  </div>
                )}
                {prepData && (
                  <div>
                    <div className="mb-3">
                      <Input placeholder="Search ingredients or sub-recipes..." value={prepSearch} onChange={(e) => setPrepSearch(e.target.value)}
                        className="h-9 text-sm" data-testid="prep-search" />
                    </div>
                    <div className="max-h-[500px] overflow-y-auto border border-gray-100 rounded-lg">
                      {filteredPrepItems.sub.length > 0 && (
                        <div className="mb-2">
                          <div className="sticky top-0 bg-gray-50 px-4 py-2 text-xs font-bold text-gray-500 uppercase tracking-wider border-b">Sub-Recipes ({filteredPrepItems.sub.length})</div>
                          {filteredPrepItems.sub.map(s => {
                            const isExp = expandedPrepSubs.has(s.id);
                            const hasIngredients = s.scaled_ingredients?.length > 0;
                            return (
                              <div key={s.id}>
                                <div className={`flex items-center justify-between px-4 py-2.5 border-b border-gray-50 ${hasIngredients ? 'cursor-pointer hover:bg-gray-50' : ''}`}
                                  onClick={() => hasIngredients && toggleExpand(s.id, expandedPrepSubs, setExpandedPrepSubs)}>
                                  <div className="flex items-center gap-2">
                                    {hasIngredients ? (isExp ? <ChevronDown className="w-3.5 h-3.5 text-gray-400 shrink-0" /> : <ChevronRight className="w-3.5 h-3.5 text-gray-400 shrink-0" />) : <div className="w-3.5" />}
                                    <div>
                                      <span className="font-medium text-sm">{s.name}</span>
                                      <span className="ml-2 text-xs text-gray-400">used in: {s.used_in.join(', ')}</span>
                                    </div>
                                  </div>
                                  <span className="tabular-nums font-bold text-sm">{formatWeight(s.total_weight, s.yield_type)}</span>
                                </div>
                                {isExp && hasIngredients && (
                                  <div className="pl-10 pr-4 pb-2 bg-gray-50/50">
                                    {renderSopBlock(s.sop, `prep-sr-sop-${s.id}`)}
                                    {s.scaled_ingredients.map(ing => (
                                      <div key={ing.ingredient_name} className="flex items-center justify-between py-1.5 border-b border-gray-100 last:border-b-0">
                                        <div className="flex items-center gap-2">
                                          <span className="text-sm text-gray-700">{ing.ingredient_name}</span>
                                          <span className={`badge-pill text-[8px] ${ing.component_type === 'sub_recipe' ? 'badge-teal' : 'badge-gray'}`}>
                                            {ing.component_type === 'sub_recipe' ? 'Sub-Recipe' : 'Ingredient'}
                                          </span>
                                        </div>
                                        <span className="tabular-nums font-semibold text-sm">{formatWeight(ing.scaled_weight, ing.weight_type)}</span>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                      {filteredPrepItems.raw.length > 0 && (
                        <div>
                          <div className="sticky top-0 bg-gray-50 px-4 py-2 text-xs font-bold text-gray-500 uppercase tracking-wider border-b">Raw Ingredients ({filteredPrepItems.raw.length})</div>
                          {filteredPrepItems.raw.map(i => (
                            <div key={i.ingredient_name} className="flex items-center justify-between px-4 py-2.5 border-b border-gray-50 hover:bg-gray-50">
                              <div>
                                <span className="font-medium text-sm">{i.ingredient_name}</span>
                                {i.supplier && <span className="ml-2 badge-pill badge-gray text-[9px]">{i.supplier}</span>}
                              </div>
                              <span className="tabular-nums font-bold text-sm">{formatWeight(i.total_weight, i.unit_type)}</span>
                            </div>
                          ))}
                        </div>
                      )}
                      {filteredPrepItems.raw.length === 0 && filteredPrepItems.sub.length === 0 && (
                        <div className="text-center py-8 text-gray-400 text-sm">No results{prepSearch ? ' for search' : ''}</div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* ===== FEATURE 3: CLIENT RECIPE BREAKDOWN ===== */}
          <div className="mb-8">
            <h2 className="text-lg font-semibold mb-1" style={{ fontFamily: 'var(--font-heading)' }}>Client Recipe Breakdown</h2>
            <p className="text-xs text-gray-500 mb-3">Final recipe quantities and full component breakdown by client and production day</p>
            <div className="bg-white border border-gray-200 rounded-lg p-4" data-testid="client-breakdown">
              <div className="flex flex-wrap items-center gap-2 mb-3">
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider mr-1">Days:</span>
                {PRODUCTION_DAYS.map(d => (
                  <button key={d} onClick={() => { toggleCbDay(d); const next = new Set(cbDays); if (next.has(d)) next.delete(d); else next.add(d); loadCbClients(next); }}
                    className={`px-3 py-1.5 text-xs font-semibold rounded-full border transition-colors ${cbDays.has(d) ? 'bg-[var(--color-primary)] text-white border-[var(--color-primary)]' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-400'}`}
                    data-testid={`cb-day-${d}`}>{d}</button>
                ))}
              </div>
              {cbAvailableClients.length > 0 && (
                <div className="mb-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Clients ({cbClients.size}/{cbAvailableClients.length})</span>
                    <div className="flex gap-2">
                      <button onClick={() => setCbClients(new Set(cbAvailableClients))} className="text-xs text-[var(--color-primary)] hover:underline" data-testid="cb-select-all">Select All</button>
                      <button onClick={() => setCbClients(new Set())} className="text-xs text-gray-400 hover:underline" data-testid="cb-deselect-all">Deselect All</button>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {cbAvailableClients.map(name => (
                      <button key={name} onClick={() => setCbClients(prev => { const n = new Set(prev); if (n.has(name)) n.delete(name); else n.add(name); return n; })}
                        className={`px-2.5 py-1 text-xs rounded-full border transition-colors ${cbClients.has(name) ? 'bg-[var(--color-primary-50)] text-[var(--color-primary-dark)] border-[var(--color-primary)]' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-400'}`}
                        data-testid={`cb-client-${name}`}>{name}</button>
                    ))}
                  </div>
                </div>
              )}
              <Button onClick={handleCbGenerate} disabled={cbDays.size === 0 || cbLoading || !selectedOrderId} className="btn-primary text-xs h-8" data-testid="cb-generate-btn">
                {cbLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Calculator className="w-3.5 h-3.5 mr-1" />}
                Generate Breakdown
              </Button>
              {cbData && cbData.recipes?.length > 0 && (
                <div className="mt-4 space-y-2">
                  <div className="text-xs text-gray-500">{cbData.recipes.length} recipes, {cbData.recipes.reduce((s, r) => s + r.total_quantity, 0)} total portions</div>
                  {cbData.recipes.map(r => {
                    const rKey = r.recipe_id;
                    const isExp = expandedCbRecipes.has(rKey);
                    const sizeEntries = Object.entries(r.size_breakdown || {});
                    return (
                      <div key={rKey} className="border border-gray-100 rounded-lg overflow-hidden">
                        <div className="flex items-center gap-2 p-3 cursor-pointer hover:bg-gray-50" onClick={() => toggleExpand(rKey, expandedCbRecipes, setExpandedCbRecipes)}>
                          {isExp ? <ChevronDown className="w-4 h-4 text-gray-400 shrink-0" /> : <ChevronRight className="w-4 h-4 text-gray-400 shrink-0" />}
                          <span className="font-medium text-sm flex-1">{r.dish_name}</span>
                          {r.is_vegetarian && <span className="text-green-600 text-xs">(v)</span>}
                          <span className="badge-pill badge-teal text-[9px]">{normalizeCategory(r.category)}</span>
                          <span className="tabular-nums font-bold">{r.total_quantity} portions</span>
                        </div>
                        {/* Size breakdown row */}
                        {sizeEntries.length > 1 && (
                          <div className="px-4 pb-1 -mt-1 flex gap-3">
                            {sizeEntries.map(([size, qty]) => (
                              <span key={size} className="text-[10px] text-gray-400">{size}: {qty}</span>
                            ))}
                          </div>
                        )}
                        <StickerLine stickers={r.stickers} testId={`cb-fr-stickers-${rKey}`} className="mx-4 mb-2" />
                        {isExp && r.components?.length > 0 && (
                          <div className="px-4 pb-3 border-t border-gray-100 bg-gray-50/50">
                            {renderSopBlock(r.recipe_sop, `cb-fr-sop-${rKey}`)}
                            {r.components.map(c => {
                              const cKey = `${rKey}|${c.component_name}`;
                              const hasCh = c.children?.length > 0;
                              const cExp = expandedCbChildren.has(cKey);
                              return (
                                <div key={c.component_name} className="border-b border-gray-100 last:border-b-0">
                                  <div className={`flex items-center gap-2 py-2 ${hasCh ? 'cursor-pointer' : ''}`}
                                    onClick={() => hasCh && toggleExpand(cKey, expandedCbChildren, setExpandedCbChildren)}>
                                    {hasCh ? (cExp ? <ChevronDown className="w-3.5 h-3.5 text-gray-400" /> : <ChevronRight className="w-3.5 h-3.5 text-gray-400" />) : <div className="w-3.5" />}
                                    <span className="text-sm flex-1">{c.component_name}</span>
                                    <span className={`badge-pill text-[9px] ${c.component_type === 'sub_recipe' ? 'badge-teal' : 'badge-gray'}`}>{c.component_type === 'sub_recipe' ? 'Sub-Recipe' : 'Ingredient'}</span>
                                    <span className="tabular-nums font-bold text-sm">{formatWeight(c.total_weight, c.weight_type)}</span>
                                  </div>
                                  {cExp && hasCh && (
                                    <div className="pl-6 pb-2">{renderCalcTree(c.children, 1)}</div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
              {cbData && cbData.recipes?.length === 0 && (
                <div className="mt-4 text-center py-6 text-gray-400 text-sm">No recipes found for selected filters</div>
              )}
            </div>
          </div>

          {/* ===== CALCULATORS (Sub-Recipe + Final Recipe) ===== */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* LEFT: Sub-Recipe Calculator */}
            <div>
              <h2 className="text-lg font-semibold mb-3" style={{ fontFamily: 'var(--font-heading)' }}>Sub-Recipe Calculator</h2>
              <p className="text-xs text-gray-500 mb-4">Calculate ingredient quantities for a desired sub-recipe weight.</p>
              <div className="flex gap-2 items-end mb-4">
                <div className="flex-1">
                  <label className="text-xs text-gray-500 mb-1 block">Sub-Recipe</label>
                  <Popover open={calcSearchOpen} onOpenChange={setCalcSearchOpen}>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-full justify-between text-sm font-normal h-10" data-testid="calc-sub-recipe">
                        <span className="truncate">{calcSubRecipeId ? allSubRecipes.find(s => s.id === calcSubRecipeId)?.name || 'Select...' : 'Search sub-recipe...'}</span>
                        <Search className="w-4 h-4 ml-2 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[400px] p-0" style={{ borderRadius: '12px' }}>
                      <Command>
                        <CommandInput placeholder="Search sub-recipes..." />
                        <CommandList>
                          <CommandEmpty>Not found.</CommandEmpty>
                          <CommandGroup>
                            {allSubRecipes.map(sr => (
                              <CommandItem key={sr.id} value={sr.name} onSelect={() => { setCalcSubRecipeId(sr.id); setCalcSearchOpen(false); }}>
                                <span className="truncate flex-1">{sr.name}</span>
                                <span className="text-xs text-gray-400 shrink-0 ml-2">{sr.yield_amount}{sr.yield_type}</span>
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                </div>
                <div className="w-28">
                  <label className="text-xs text-gray-500 mb-1 block">Weight (g)</label>
                  <Input type="number" value={calcWeight} onChange={(e) => setCalcWeight(e.target.value)} placeholder="5000" className="tabular-nums h-10" data-testid="calc-weight" />
                </div>
                <Button onClick={handleCalculate} className="btn-primary h-10" data-testid="calc-btn"><Calculator className="w-4 h-4 mr-1" /> Go</Button>
              </div>
              {calcResult && (
                <div className="bg-white border border-gray-200 rounded-lg p-4" data-testid="calc-result">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-semibold">{calcResult.sub_recipe_name}</h3>
                    <Button onClick={() => exportCalcResult('sub')} variant="outline" className="text-xs h-7" style={{ borderRadius: '9999px' }}><Download className="w-3 h-3 mr-1" /> Export</Button>
                  </div>
                  <div className="text-xs text-gray-500 mb-3">Yield: {calcResult.yield_amount}{calcResult.yield_type} | Needed: {formatWeight(calcResult.desired_weight)} | Scale: {calcResult.scale_factor}x</div>
                  {renderSopBlock(calcResult.sop, 'calc-sub-sop')}
                  {renderCalcTree(calcResult.ingredients, 0)}
                </div>
              )}
            </div>

            {/* RIGHT: Final Recipe Calculator (FEATURE 4: Multi-Size) */}
            <div>
              <h2 className="text-lg font-semibold mb-3" style={{ fontFamily: 'var(--font-heading)' }}>Final Recipe Calculator</h2>
              <p className="text-xs text-gray-500 mb-4">Calculate all components across size variations. Enter quantities for each size.</p>
              <div className="mb-3">
                <label className="text-xs text-gray-500 mb-1 block">Final Recipe</label>
                <Popover open={calcRecipeSearchOpen} onOpenChange={setCalcRecipeSearchOpen}>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-between text-sm font-normal h-10" data-testid="calc-recipe">
                      <span className="truncate">{calcRecipeId ? allRecipes.find(r => r.id === calcRecipeId)?.name || 'Select...' : 'Search recipe...'}</span>
                      <Search className="w-4 h-4 ml-2 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[400px] p-0" style={{ borderRadius: '12px' }}>
                    <Command>
                      <CommandInput placeholder="Search recipes..." />
                      <CommandList>
                        <CommandEmpty>Not found.</CommandEmpty>
                        <CommandGroup>
                          {allRecipes.map(r => (
                            <CommandItem key={r.id} value={r.name} onSelect={() => {
                              setCalcRecipeId(r.id);
                              setCalcRecipeSearchOpen(false);
                              setCalcMultiResult(null);
                              // Initialize size quantity inputs
                              const sizes = r.size_variations?.map(sv => sv.size_name) || [];
                              const initQtys = {};
                              sizes.forEach(s => { initQtys[s] = ''; });
                              setCalcMultiSizeQtys(initQtys);
                            }}>
                              <span className="truncate flex-1">{r.name}</span>
                              <span className="text-xs text-gray-400 shrink-0 ml-2">{normalizeCategory(r.category)}</span>
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>
              {/* Multi-size quantity inputs */}
              {calcRecipeId && (() => {
                const recipe = allRecipes.find(r => r.id === calcRecipeId);
                const sizes = recipe?.size_variations?.map(sv => sv.size_name) || [];
                return sizes.length > 0 ? (
                  <div className="mb-4">
                    <div className="flex flex-wrap gap-3 items-end">
                      {sizes.map(s => (
                        <div key={s} className="w-28">
                          <label className="text-xs text-gray-500 mb-1 block">{s}</label>
                          <Input type="number" min="0" placeholder="0"
                            value={calcMultiSizeQtys[s] || ''}
                            onChange={(e) => setCalcMultiSizeQtys(prev => ({ ...prev, [s]: e.target.value }))}
                            className="tabular-nums h-10" data-testid={`calc-multi-${s}`} />
                        </div>
                      ))}
                      <Button onClick={handleCalculateMulti} className="btn-primary h-10" data-testid="calc-multi-btn"><Calculator className="w-4 h-4 mr-1" /> Calculate</Button>
                    </div>
                  </div>
                ) : null;
              })()}
              {/* Multi-size result */}
              {calcMultiResult && (
                <div className="bg-white border border-gray-200 rounded-lg p-4" data-testid="calc-multi-result">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-semibold">{calcMultiResult.recipe_name}</h3>
                  </div>
                  <div className="text-xs text-gray-500 mb-3">
                    {calcMultiResult.size_quantities.map(sq => `${sq.meal_size}: ${sq.quantity}`).join(' + ')} = {calcMultiResult.total_portions} total portions
                  </div>
                  {renderSopBlock(calcMultiResult.sop, 'calc-multi-sop')}
                  {calcMultiResult.components?.map((c) => {
                    const isExp = expandedMultiComponents.has(c.component_name);
                    const hasCh = c.children?.length > 0;
                    return (
                      <div key={c.component_name} className="border-b border-gray-100 last:border-b-0">
                        <div className={`flex items-center gap-2 py-2 ${hasCh ? 'cursor-pointer hover:bg-gray-50' : ''}`}
                          onClick={() => hasCh && toggleExpand(c.component_name, expandedMultiComponents, setExpandedMultiComponents)}>
                          {hasCh ? (isExp ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />) : <div className="w-4" />}
                          <span className="font-medium text-sm flex-1">{c.component_name}</span>
                          <span className={`badge-pill text-[9px] ${c.component_type === 'sub_recipe' ? 'badge-teal' : 'badge-gray'}`}>{c.component_type === 'sub_recipe' ? 'Sub-Recipe' : 'Ingredient'}</span>
                          <span className="tabular-nums font-bold text-base">{formatWeight(c.total_weight, c.weight_type)}</span>
                        </div>
                        {/* Per-size breakdown */}
                        {c.per_size && Object.keys(c.per_size).length > 1 && (
                          <div className="pl-6 flex gap-3 pb-1">
                            {Object.entries(c.per_size).map(([sz, info]) => (
                              <span key={sz} className="text-[10px] text-gray-400">{sz}: {info.quantity} x {formatWeight(info.weight_per_portion, c.weight_type)} = {formatWeight(info.total_weight, c.weight_type)}</span>
                            ))}
                          </div>
                        )}
                        {isExp && hasCh && (
                          <div className="pl-6 pb-2">
                            {renderCalcTree(c.children, 1)}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </TabsContent>

        {/* ============ PACKING TAB ============ */}
        <TabsContent value="PACKING">
          <PackingTab selectedOrderId={selectedOrderId} />
        </TabsContent>

        {/* ============ CLIENT PACKING TAB ============ */}
        <TabsContent value="CLIENT_PACKING">
          <ClientPackingTab selectedOrderId={selectedOrderId} />
        </TabsContent>



        {/* ============ INGREDIENTS TAB ============ */}
        <TabsContent value="INGREDIENTS">
          {loading ? (
            <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-[var(--color-primary)]" /></div>
          ) : sheetData ? (
            <div>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold" style={{ fontFamily: 'var(--font-heading)' }}>Weekly Ingredients</h2>
                <Button onClick={exportIngredients} className="btn-primary text-sm" data-testid="export-ingredients-btn">
                  <Download className="w-4 h-4 mr-1" /> Export Excel
                </Button>
              </div>
              {Object.entries(ingredientsBySupplier).sort(([a], [b]) => a.localeCompare(b)).map(([supplier, items]) => (
                <div key={supplier} className="mb-5">
                  <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">{supplier || 'Unknown Supplier'}</div>
                  <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                    <table className="data-table">
                      <thead><tr><th>Ingredient</th><th>Total Weight</th><th>Used In</th></tr></thead>
                      <tbody>
                        {items.sort((a, b) => a.ingredient_name.localeCompare(b.ingredient_name)).map((ri) => (
                          <tr key={ri.ingredient_name}>
                            <td className="font-medium text-sm">{ri.ingredient_name}</td>
                            <td className="tabular-nums font-semibold">{formatWeight(ri.total_weight, ri.unit_type)}</td>
                            <td className="text-xs text-gray-500">{Array.isArray(ri.used_in) ? ri.used_in.join(', ') : ri.used_in}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-20 text-gray-400"><p className="text-sm">Select a week to view ingredients</p></div>
          )}
        </TabsContent>

        {/* ============ ALL + DAILY TABS ============ */}
        {['ALL', ...PRODUCTION_DAYS].map(day => (
          <TabsContent key={day} value={day}>
            {loading ? (
              <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-[var(--color-primary)]" /></div>
            ) : sheetData ? (
              <div>
                {/* Summary Cards */}
                <div className="flex items-center justify-between mb-4">
                  <div className="flex gap-3 flex-1">
                    <div className="bg-white border border-gray-200 rounded-lg p-4 flex-1">
                      <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">Dishes</div>
                      <div className="text-2xl font-bold tabular-nums">{sheetData?.summary?.total_dishes || 0}</div>
                    </div>
                    <div className="bg-white border border-gray-200 rounded-lg p-4 flex-1">
                      <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">Portions</div>
                      <div className="text-2xl font-bold tabular-nums">{sheetData?.summary?.total_portions || 0}</div>
                    </div>
                    <div className="bg-white border border-gray-200 rounded-lg p-4 flex-1">
                      <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">Sub-Recipes</div>
                      <div className="text-2xl font-bold tabular-nums">{sheetData.sub_recipes?.length || 0}</div>
                    </div>
                    {isDayTab && (
                      <div className="bg-white border border-gray-200 rounded-lg p-4 flex-1">
                        <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">Progress</div>
                        <div className="text-2xl font-bold tabular-nums">{progressPct}%</div>
                        {totalLines > 0 && (
                          <div className="mt-2 h-2 bg-gray-100 rounded-full overflow-hidden">
                            <div className="h-full rounded-full transition-all" style={{ width: `${progressPct}%`, backgroundColor: 'var(--color-primary)' }} />
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  {isDayTab && (
                    <Button onClick={() => exportDailySheet(day)} className="btn-primary text-sm ml-4" data-testid={`export-day-${day}`}>
                      <Download className="w-4 h-4 mr-1" /> Export Excel
                    </Button>
                  )}
                </div>

                {/* A) Final Recipe Breakdown (daily tabs only) */}
                {isDayTab && (
                  <div className="mb-8">
                    <h2 className="text-lg font-semibold mb-3" style={{ fontFamily: 'var(--font-heading)' }}>Final Recipes</h2>
                    {sheetData.summary.unlinked_count > 0 && (
                      <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg mb-3 text-sm text-amber-700 flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4" /> {sheetData.summary.unlinked_count} dish(es) not linked to a recipe
                      </div>
                    )}
                    {Object.entries(groupedRecipes).map(([cat, recipes]) => (
                      <div key={cat} className="mb-4">
                        <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">{cat}</div>
                        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                          {recipes.map(fr => {
                            const isExpanded = expandedRecipes.has(fr.line_id);
                            return (
                              <div key={fr.line_id} className={`border-b border-gray-100 last:border-b-0 ${fr.client_allergy_notes?.length ? 'border-l-4 border-l-red-400' : ''}`}>
                                <div className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 cursor-pointer"
                                  onClick={() => toggleExpand(fr.line_id, expandedRecipes, setExpandedRecipes)}>
                                  <Checkbox checked={!!lineStatuses[fr.line_id]}
                                    onCheckedChange={() => toggleLineStatus(fr.line_id)}
                                    onClick={(e) => e.stopPropagation()} />
                                  {fr.is_linked ? (isExpanded ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />) : <AlertTriangle className="w-4 h-4 text-amber-400" />}
                                  <div className="flex-1 min-w-0">
                                    <span className={`font-medium text-sm ${lineStatuses[fr.line_id] ? 'line-through text-gray-400' : ''}`}>
                                      {fr.dish_name}
                                    </span>
                                    {fr.is_vegetarian && <span className="ml-1 text-green-600 text-xs">(v)</span>}
                                  </div>
                                  {fr.meal_size && <span className="badge-pill badge-gray text-[10px]">{fr.meal_size}</span>}
                                  <span className="tabular-nums font-bold text-base w-20 text-right">{fr.total_quantity} ptn</span>
                                </div>
                                {fr.client_allergy_notes?.length > 0 && (
                                  <div className="mx-4 mb-2 ml-14 p-2 bg-red-50 border border-red-200 rounded-lg">
                                    {fr.client_allergy_notes.map((ca, ci) => (
                                      <div key={ci} className="flex items-center gap-1.5 text-xs text-red-700">
                                        <AlertTriangle className="w-3 h-3 shrink-0" />
                                        <span className="font-bold">{ca.client_name}:</span>
                                        <span>{ca.allergy_notes}</span>
                                      </div>
                                    ))}
                                  </div>
                                )}
                                <StickerLine stickers={fr.stickers} testId={`fr-stickers-${fr.line_id}`} className="mx-4 mb-2 ml-14" />
                                {isExpanded && fr.components.length > 0 && (
                                  <div className="px-4 pb-3 ml-14">
                                    {renderSopBlock(fr.recipe_sop, `fr-sop-${fr.line_id}`)}
                                    <table className="w-full text-sm">
                                      <thead><tr className="text-gray-400 text-xs"><th className="text-left py-1 font-medium">Component</th><th className="text-left py-1 font-medium">Type</th><th className="text-right py-1 font-medium">Total Needed</th></tr></thead>
                                      <tbody>
                                        {fr.components.map((c) => (
                                          <tr key={c.component_name} className="border-t border-gray-100">
                                            <td className="py-1.5 font-medium">{c.component_name}</td>
                                            <td className="py-1.5"><span className={`badge-pill text-[9px] ${c.component_type === 'sub_recipe' ? 'badge-teal' : 'badge-gray'}`}>{c.component_type === 'sub_recipe' ? 'Sub-Recipe' : 'Ingredient'}</span></td>
                                            <td className="py-1.5 text-right tabular-nums font-bold text-base">{formatWeight(c.total_weight, c.weight_type)}</td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* B) Sub-Recipe Summary */}
                <div className="mb-8">
                  <div className="flex items-center justify-between mb-3">
                    <h2 className="text-lg font-semibold" style={{ fontFamily: 'var(--font-heading)' }}>
                      {day === 'ALL' ? 'Weekly Sub-Recipe Totals' : 'Sub-Recipe Summary'}
                    </h2>
                    {day === 'ALL' && (sheetData.sub_recipes || []).length > 0 && (
                      <div className="flex items-center gap-2">
                        <button onClick={selectedSubRecipes.size === (sheetData.sub_recipes || []).length ? deselectAllSubRecipes : selectAllSubRecipes}
                          className="text-xs font-bold text-[var(--color-primary)] hover:underline" data-testid="select-all-sr-btn">
                          {selectedSubRecipes.size === (sheetData.sub_recipes || []).length ? 'Deselect All' : 'Select All'}
                        </button>
                        <Button onClick={exportSelectedSubRecipes} disabled={selectedSubRecipes.size === 0 || exportingSR}
                          className="btn-primary text-sm" data-testid="export-sub-recipes-btn">
                          {exportingSR ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Download className="w-4 h-4 mr-1" />}
                          Export ({selectedSubRecipes.size})
                        </Button>
                      </div>
                    )}
                  </div>
                  {(sheetData.sub_recipes || []).length === 0 ? (
                    <div className="text-sm text-gray-400 text-center py-8">No sub-recipes needed.</div>
                  ) : (
                    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                      {(sheetData.sub_recipes || []).map(sr => {
                        const isExpanded = expandedSubRecipes.has(sr.id);
                        return (
                          <div key={sr.id} className="border-b border-gray-100 last:border-b-0">
                            <div className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 cursor-pointer"
                              onClick={() => toggleExpand(sr.id, expandedSubRecipes, setExpandedSubRecipes)}>
                              {day === 'ALL' && (
                                <Checkbox checked={selectedSubRecipes.has(sr.id)}
                                  onCheckedChange={() => toggleSubRecipeSelection(sr.id)}
                                  onClick={(e) => e.stopPropagation()} />
                              )}
                              {isDayTab && (
                                <Checkbox checked={!!subRecipeStatuses[sr.id]}
                                  onCheckedChange={() => toggleSubRecipeStatus(sr.id)}
                                  onClick={(e) => e.stopPropagation()} />
                              )}
                              {isExpanded ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
                              <span className={`font-medium text-sm flex-1 ${subRecipeStatuses[sr.id] ? 'line-through text-gray-400' : ''}`}>{sr.name}</span>
                              <span className="tabular-nums font-bold text-base text-[var(--color-primary-dark)]">{formatWeight(sr.total_weight, sr.yield_type)}</span>
                              {day === 'ALL' && Object.keys(sr.by_day || {}).length > 0 && (
                                <div className="flex gap-1 ml-2">
                                  {Object.entries(sr.by_day).map(([d, w]) => (
                                    <span key={d} className="badge-pill badge-gray text-[9px]">{d}: {formatWeight(w)}</span>
                                  ))}
                                </div>
                              )}
                            </div>
                            {isExpanded && (
                              <div className="px-4 pb-3 ml-14">
                                {renderSopBlock(sr.sop, `sr-sop-${sr.id}`)}
                                <div className="text-xs text-gray-500 mb-2">
                                  Used in: {sr.used_in?.map(u => `${u.dish} (${formatWeight(u.weight)})`).join(', ')}
                                </div>
                                <table className="w-full text-sm">
                                  <thead><tr className="text-gray-400 text-xs"><th className="text-left py-1 font-medium">Ingredient</th><th className="text-right py-1 font-medium">Total Needed</th></tr></thead>
                                  <tbody>
                                    {sr.scaled_ingredients?.map((ing) => (
                                      <tr key={ing.ingredient_name} className="border-t border-gray-100">
                                        <td className="py-1.5 font-medium">
                                          {ing.ingredient_name}
                                          {ing.component_type === 'sub_recipe' && <span className="ml-1 badge-pill badge-teal text-[9px]">Sub-Recipe</span>}
                                        </td>
                                        <td className="py-1.5 text-right tabular-nums font-bold text-base">{formatWeight(ing.scaled_weight, ing.weight_type)}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Issue Reporting (daily tabs only) */}
                {isDayTab && (
                  <div className="mb-8">
                    <h2 className="text-lg font-semibold mb-3" style={{ fontFamily: 'var(--font-heading)' }}>Issue Reporting</h2>
                    <div className="bg-white border border-gray-200 rounded-lg p-4">
                      {issues.length > 0 && (
                        <div className="mb-4 space-y-2">
                          {issues.map((issue) => (
                            <div key={issue.timestamp} className="flex items-start gap-2 p-2 bg-gray-50 rounded-lg">
                              <Clock className="w-4 h-4 text-gray-400 mt-0.5 shrink-0" />
                              <div>
                                <div className="text-xs text-gray-400">{new Date(issue.timestamp).toLocaleString()}</div>
                                <div className="text-sm">{issue.text}</div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                      <div className="flex gap-2">
                        <Textarea value={issueText} onChange={(e) => setIssueText(e.target.value)}
                          placeholder="Log an issue (e.g. Ran out of Frozen Peas, substituted with Fresh)..."
                          className="min-h-[60px] flex-1" data-testid="issue-input" />
                        <Button onClick={addIssue} className="btn-primary self-end" data-testid="add-issue-btn">
                          <MessageSquare className="w-4 h-4 mr-1" /> Log
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-20 text-gray-400">
                <ClipboardList className="w-16 h-16 mx-auto mb-3 opacity-20" />
                <p className="text-sm">Select a week to view production sheets</p>
              </div>
            )}
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
};

export default ProductionPage;
